import { useState, useEffect } from 'react';
import { useQuery, useMutation } from '@apollo/client';
import DashboardLayout from '../../components/layout/DashboardLayout';
import { ADMIN_PLATFORM_SETTINGS_QUERY, ADMIN_ALL_USER_PLANS_QUERY, ADMIN_EMAIL_SETTINGS_QUERY, ADMIN_SAFETY_LIMITS_QUERY, ADMIN_ARCHIVE_SETTINGS_QUERY, ADMIN_BASEMAP_SETTINGS_QUERY, ADMIN_DELIVERY_SETTINGS_QUERY, ADMIN_TRASH_SETTINGS_QUERY, ADMIN_TRASH_OVERVIEW_QUERY, SCENE_SETTINGS_QUERY } from '../../graphql/queries';
import { ADMIN_UPDATE_PLATFORM_SETTINGS_MUTATION, ADMIN_UPDATE_EMAIL_SETTINGS_MUTATION, ADMIN_SEND_TEST_EMAIL_MUTATION, ADMIN_UPDATE_SAFETY_LIMITS_MUTATION, ADMIN_UPDATE_ARCHIVE_SETTINGS_MUTATION, ADMIN_UPDATE_BASEMAP_SETTINGS_MUTATION, ADMIN_UPDATE_DELIVERY_SETTINGS_MUTATION, ADMIN_UPDATE_TRASH_SETTINGS_MUTATION, ADMIN_UPDATE_SCENE_SETTINGS_MUTATION } from '../../graphql/mutations';
import { formatBytes } from '../../utils/trash';
import { NO_PERMISSIONS, PERMISSIONS_META, Permissions, permissionsOf } from '../../constants/permissions';
import FileFormatsSettings from './FileFormatsSettings';
import styles from './AdminPlatformSettings.module.css';
import Icon from '../../components/common/Icon';

interface UserPlanOption {
  id: string;
  name: string;
}

// Every safety limit is a hard cap enforced server-side; 0 means no limit.
type SafetyKey =
  | 'maxFilesPointCloud' | 'maxFilesGaussian' | 'maxFiles2d' | 'maxFiles3d' | 'maxFilesDocument'
  | 'maxFilesOrthomosaic' | 'maxFilesVideo360'
  | 'maxSizePointCloudMb' | 'maxSizeGaussianMb' | 'maxSize2dMb' | 'maxSize3dMb' | 'maxSizeDocumentMb'
  | 'maxSizeOrthomosaicMb' | 'maxSizeVideo360Mb'
  | 'maxNotesPointCloud' | 'maxNotesGaussian' | 'maxAttachmentsPerNote' | 'maxAttachmentSizeMb'
  | 'maxGuestNotesPerHour' | 'maxSharedLinksPerProject';

const SAFETY_GROUPS: { title: string; hint: string; fields: { key: SafetyKey; label: string }[] }[] = [
  {
    title: 'Files per project',
    hint: 'How many files of each kind a single project may hold.',
    fields: [
      { key: 'maxFilesPointCloud', label: 'Point clouds' },
      { key: 'maxFilesGaussian', label: 'Gaussian splats' },
      { key: 'maxFiles2d', label: '2D drawings (DWG/DXF)' },
      { key: 'maxFiles3d', label: '3D models (IFC/RVT/…)' },
      { key: 'maxFilesDocument', label: 'Documents (PDF/…)' },
      { key: 'maxFilesOrthomosaic', label: 'Orthomosaics (GeoTIFF)' },
      { key: 'maxFilesVideo360', label: '360 videos (MP4/MOV)' },
    ],
  },
  {
    title: 'Maximum file size (MB)',
    hint: 'Per uploaded file, by section. Uploads above the cap are refused before any data is stored.',
    fields: [
      { key: 'maxSizePointCloudMb', label: 'Point clouds' },
      { key: 'maxSizeGaussianMb', label: 'Gaussian splats' },
      { key: 'maxSize2dMb', label: '2D drawings' },
      { key: 'maxSize3dMb', label: '3D models' },
      { key: 'maxSizeDocumentMb', label: 'Documents' },
      { key: 'maxSizeOrthomosaicMb', label: 'Orthomosaics' },
      { key: 'maxSizeVideo360Mb', label: '360 videos' },
    ],
  },
  {
    title: 'Notes',
    hint: 'Caps on notes and their attached PDFs, including notes added through share links.',
    fields: [
      { key: 'maxNotesPointCloud', label: 'Notes per point cloud file' },
      { key: 'maxNotesGaussian', label: 'Notes per Gaussian splat' },
      { key: 'maxAttachmentsPerNote', label: 'Files per note' },
      { key: 'maxAttachmentSizeMb', label: 'Max attachment size (MB)' },
      { key: 'maxGuestNotesPerHour', label: 'Guest notes per hour, per share link' },
    ],
  },
  {
    title: 'Sharing',
    hint: 'Limits on share links.',
    fields: [
      { key: 'maxSharedLinksPerProject', label: 'Active share links per project' },
    ],
  },
];

const EMPTY_SAFETY = Object.fromEntries(
  SAFETY_GROUPS.flatMap((g) => g.fields.map((f) => [f.key, 0])),
) as Record<SafetyKey, number>;

interface ArchiveForm {
  enabled: boolean;
  delayedDownloadDays: number;
  fullArchiveDays: number;
  restoreKeepDays: number;
}

// Mirrors the server's rules, which would otherwise silently clamp the values.
const archiveProblem = (a: ArchiveForm): string => {
  if (a.delayedDownloadDays < 30) return 'Delayed download must be at least 30 days.';
  if (a.fullArchiveDays !== 0 && a.fullArchiveDays < a.delayedDownloadDays + 30) {
    return `Full archive must be 0 (never) or at least 30 days after delayed download (${a.delayedDownloadDays + 30} days or more).`;
  }
  if (a.restoreKeepDays < 1 || a.restoreKeepDays > 30) return 'Restored copies can be kept for 1 to 30 days.';
  return '';
};

const wholeDays = (value: string): number => Math.max(0, Math.floor(Number(value) || 0));

interface DeliveryForm {
  intelligentTiering: boolean;
  downloadsViaCloudfront: boolean;
}

interface DeliveryInfo extends DeliveryForm {
  intelligentTieringSince: string | null;
  cloudfrontConfigured: boolean;
  maxCdnDownloadGb: number;
}

const sinceDate = (iso: string) => new Date(iso).toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' });

const loadFailed = (what: string, message: string) => `Could not load the ${what}: ${message}. Saving is disabled until they load — reload the page.`;

const AdminPlatformSettings = () => {
  const { data, loading, error: settingsError } = useQuery(ADMIN_PLATFORM_SETTINGS_QUERY);
  const { data: plansData } = useQuery(ADMIN_ALL_USER_PLANS_QUERY, { fetchPolicy: 'cache-first' });

  const [usersAllowedToRegister, setUsersAllowedToRegister] = useState(true);
  const [defaultUserPlanId, setDefaultUserPlanId] = useState<string>('');
  const [newUserPermissions, setNewUserPermissions] = useState<Permissions>(NO_PERMISSIONS);
  const [saved, setSaved] = useState(false);
  const [saveError, setSaveError] = useState('');

  const userPlanOptions: UserPlanOption[] = plansData?.adminAllUserPlans ?? [];

  // ── Email configuration ─────────────────────────────────────────────────────
  const { data: emailData, error: emailLoadError } = useQuery(ADMIN_EMAIL_SETTINGS_QUERY);
  const emailReady = !!emailData?.adminEmailSettings;
  const [email, setEmail] = useState({
    enabled: false,
    provider: 'ses',
    fromName: '',
    fromAddress: '',
    appUrl: '',
    sesRegion: '',
    smtpHost: '',
    smtpPort: 465,
    smtpSecure: true,
    smtpUsername: '',
    smtpPassword: '',
    smtpPasswordSet: false,
  });
  const [emailSaved, setEmailSaved] = useState(false);
  const [emailError, setEmailError] = useState('');
  const [testTo, setTestTo] = useState('');
  const [testResult, setTestResult] = useState<{ ok: boolean; error?: string } | null>(null);

  useEffect(() => {
    const e = emailData?.adminEmailSettings;
    if (e) {
      setEmail({
        enabled: e.enabled,
        provider: e.provider || 'ses',
        fromName: e.fromName || '',
        fromAddress: e.fromAddress || '',
        appUrl: e.appUrl || '',
        sesRegion: e.sesRegion || '',
        smtpHost: e.smtpHost || '',
        smtpPort: e.smtpPort || 465,
        smtpSecure: e.smtpSecure !== false,
        smtpUsername: e.smtpUsername || '',
        smtpPassword: '',
        smtpPasswordSet: e.smtpPasswordSet,
      });
    }
  }, [emailData]);

  const [updateEmailSettings, { loading: savingEmail }] = useMutation(ADMIN_UPDATE_EMAIL_SETTINGS_MUTATION, {
    onCompleted: () => {
      setEmailSaved(true);
      setTimeout(() => setEmailSaved(false), 2500);
    },
    onError: (err) => setEmailError(err.message),
  });

  const [sendTestEmail, { loading: sendingTest }] = useMutation(ADMIN_SEND_TEST_EMAIL_MUTATION, {
    onCompleted: (d) => setTestResult(d.adminSendTestEmail),
    onError: (err) => setTestResult({ ok: false, error: err.message }),
  });

  const handleSaveEmail = async () => {
    setTestResult(null);
    setEmailError('');
    await updateEmailSettings({
      variables: {
        input: {
          enabled: email.enabled,
          provider: email.provider,
          fromName: email.fromName,
          fromAddress: email.fromAddress,
          appUrl: email.appUrl,
          sesRegion: email.sesRegion || null,
          smtpHost: email.smtpHost || null,
          smtpPort: Number(email.smtpPort) || null,
          smtpSecure: email.smtpSecure,
          smtpUsername: email.smtpUsername || null,
          smtpPassword: email.smtpPassword || null,
        },
      },
    });
  };

  // ── Safety configuration ────────────────────────────────────────────────────
  const { data: safetyData, error: safetyLoadError } = useQuery(ADMIN_SAFETY_LIMITS_QUERY);
  const safetyReady = !!safetyData?.adminSafetyLimits;
  const [safety, setSafety] = useState<Record<SafetyKey, number>>(EMPTY_SAFETY);
  const [safetySaved, setSafetySaved] = useState(false);
  const [safetyError, setSafetyError] = useState('');

  useEffect(() => {
    const s = safetyData?.adminSafetyLimits;
    if (!s) return;
    setSafety(Object.fromEntries(
      (Object.keys(EMPTY_SAFETY) as SafetyKey[]).map((k) => [k, Number(s[k]) || 0]),
    ) as Record<SafetyKey, number>);
  }, [safetyData]);

  const [updateSafety, { loading: savingSafety }] = useMutation(ADMIN_UPDATE_SAFETY_LIMITS_MUTATION, {
    onCompleted: () => {
      setSafetySaved(true);
      setTimeout(() => setSafetySaved(false), 2500);
    },
    onError: (err) => setSafetyError(err.message),
  });

  // ── Storage & delivery ──────────────────────────────────────────────────────
  const { data: deliveryData, error: deliveryLoadError } = useQuery(ADMIN_DELIVERY_SETTINGS_QUERY);
  const [deliveryInfo, setDeliveryInfo] = useState<DeliveryInfo | null>(null);
  const [delivery, setDelivery] = useState<DeliveryForm>({ intelligentTiering: false, downloadsViaCloudfront: false });
  const [deliverySaved, setDeliverySaved] = useState(false);
  const [deliveryError, setDeliveryError] = useState('');
  const applyDelivery = (d: DeliveryInfo) => {
    setDeliveryInfo(d);
    setDelivery({ intelligentTiering: d.intelligentTiering, downloadsViaCloudfront: d.downloadsViaCloudfront });
  };
  useEffect(() => {
    const d = deliveryData?.adminDeliverySettings;
    if (d) applyDelivery(d);
  }, [deliveryData]);
  const [updateDelivery, { loading: savingDelivery }] = useMutation(ADMIN_UPDATE_DELIVERY_SETTINGS_MUTATION, {
    onCompleted: (d) => {
      if (d?.adminUpdateDeliverySettings) applyDelivery(d.adminUpdateDeliverySettings);
      setDeliverySaved(true);
      setTimeout(() => setDeliverySaved(false), 2500);
    },
    onError: (err) => setDeliveryError(err.message),
  });
  const setDeliveryField = (key: keyof DeliveryForm, value: boolean) => {
    setDeliveryError('');
    setDelivery((prev) => ({ ...prev, [key]: value }));
  };

  // ── File archiving ──────────────────────────────────────────────────────────
  const { data: archiveData, error: archiveLoadError } = useQuery(ADMIN_ARCHIVE_SETTINGS_QUERY);
  const archiveReady = !!archiveData?.adminArchiveSettings;
  const [archive, setArchive] = useState<ArchiveForm>({
    enabled: false,
    delayedDownloadDays: 180,
    fullArchiveDays: 300,
    restoreKeepDays: 7,
  });
  const [archiveSaved, setArchiveSaved] = useState(false);
  const [archiveError, setArchiveError] = useState('');

  const applyArchive = (a: ArchiveForm) => setArchive({
    enabled: a.enabled,
    delayedDownloadDays: a.delayedDownloadDays,
    fullArchiveDays: a.fullArchiveDays,
    restoreKeepDays: a.restoreKeepDays,
  });

  useEffect(() => {
    const a = archiveData?.adminArchiveSettings;
    if (a) applyArchive(a);
  }, [archiveData]);

  const [updateArchive, { loading: savingArchive }] = useMutation(ADMIN_UPDATE_ARCHIVE_SETTINGS_MUTATION, {
    onCompleted: (d) => {
      if (d?.adminUpdateArchiveSettings) applyArchive(d.adminUpdateArchiveSettings);
      setArchiveSaved(true);
      setTimeout(() => setArchiveSaved(false), 2500);
    },
    onError: (err) => setArchiveError(err.message),
  });

  const setArchiveField = (key: keyof ArchiveForm, value: boolean | number) => {
    setArchiveError('');
    setArchive((prev) => ({ ...prev, [key]: value }));
  };

  const handleSaveArchive = () => {
    const problem = archiveProblem(archive);
    setArchiveError(problem);
    if (!problem) updateArchive({ variables: { input: archive } });
  };

  // ── Trash ───────────────────────────────────────────────────────────────────
  const { data: trashData, error: trashLoadError } = useQuery(ADMIN_TRASH_SETTINGS_QUERY);
  const trashReady = !!trashData?.adminTrashSettings;
  const [trash, setTrash] = useState({ retentionDays: 0, allowImmediateDelete: false, immediateDeleteGbPerMonth: 0 });
  const [trashSaved, setTrashSaved] = useState(false);
  const [trashError, setTrashError] = useState('');
  useEffect(() => {
    const t = trashData?.adminTrashSettings;
    if (t) setTrash({ retentionDays: t.retentionDays, allowImmediateDelete: t.allowImmediateDelete, immediateDeleteGbPerMonth: t.immediateDeleteGbPerMonth });
  }, [trashData]);
  const { data: trashOverviewData } = useQuery(ADMIN_TRASH_OVERVIEW_QUERY, {
    variables: { retentionDays: trash.retentionDays },
    skip: !trashReady,
    fetchPolicy: 'cache-and-network',
  });
  const trashOverview = trashOverviewData?.adminTrashOverview;
  const [updateTrash, { loading: savingTrash }] = useMutation(ADMIN_UPDATE_TRASH_SETTINGS_MUTATION, {
    refetchQueries: [{ query: ADMIN_TRASH_SETTINGS_QUERY }],
    onCompleted: () => {
      setTrashSaved(true);
      setTimeout(() => setTrashSaved(false), 2500);
    },
    onError: (err) => setTrashError(err.message),
  });
  const handleSaveTrash = () => {
    setTrashError('');
    const due = trashOverview && trash.retentionDays > 0 ? trashOverview.dueFiles + trashOverview.dueProjects : 0;
    if (due > 0 && !window.confirm(
      `${trashOverview.dueFiles} file(s) and ${trashOverview.dueProjects} project(s) (${formatBytes(trashOverview.dueBytes)}) are older than ${trash.retentionDays} days in Trash and will be removed for good at the next Trash removal run. Save?`,
    )) return;
    updateTrash({ variables: { input: trash } });
  };

  // ── Scene ───────────────────────────────────────────────────────────────────
  const { data: sceneData, error: sceneLoadError } = useQuery(SCENE_SETTINGS_QUERY);
  const sceneReady = !!sceneData?.sceneSettings;
  const [scene, setScene] = useState({ walkSpeedMps: 8, runMultiplier: 3, distanceFactor: 0.5 });
  const [sceneSaved, setSceneSaved] = useState(false);
  const [sceneError, setSceneError] = useState('');
  useEffect(() => {
    const s = sceneData?.sceneSettings;
    if (s) setScene({ walkSpeedMps: s.walkSpeedMps, runMultiplier: s.runMultiplier, distanceFactor: s.distanceFactor });
  }, [sceneData]);
  const [updateScene, { loading: savingScene }] = useMutation(ADMIN_UPDATE_SCENE_SETTINGS_MUTATION, {
    refetchQueries: [{ query: SCENE_SETTINGS_QUERY }],
    onCompleted: () => {
      setSceneSaved(true);
      setTimeout(() => setSceneSaved(false), 2500);
    },
    onError: (err) => setSceneError(err.message),
  });
  const handleSaveScene = () => {
    const problem = !(scene.walkSpeedMps > 0) ? 'Walking speed must be above 0 m/s'
      : !(scene.runMultiplier >= 1) ? 'The run multiplier must be at least 1'
      : !(scene.distanceFactor >= 0) ? 'The speed-up with distance must be 0 or more'
      : '';
    setSceneError(problem);
    if (!problem) updateScene({ variables: { input: scene } });
  };

  // ── Base map ────────────────────────────────────────────────────────────────
  const { data: basemapData, error: basemapLoadError } = useQuery(ADMIN_BASEMAP_SETTINGS_QUERY);
  const basemapReady = !!basemapData?.adminBasemapSettings;
  const [basemap, setBasemap] = useState({ esriApiKey: '', googleApiKey: '', terrainEnabled: true });
  const [basemapSaved, setBasemapSaved] = useState(false);
  const [basemapError, setBasemapError] = useState('');
  useEffect(() => {
    const b = basemapData?.adminBasemapSettings;
    if (!b) return;
    setBasemap({ esriApiKey: b.esriApiKey ?? '', googleApiKey: b.googleApiKey ?? '', terrainEnabled: b.terrainEnabled !== false });
  }, [basemapData]);
  const googleStatus: string = basemapData?.adminBasemapSettings?.googleStatus ?? 'unset';
  const [updateBasemap, { loading: savingBasemap }] = useMutation(ADMIN_UPDATE_BASEMAP_SETTINGS_MUTATION, {
    refetchQueries: [{ query: ADMIN_BASEMAP_SETTINGS_QUERY }],
    onCompleted: () => {
      setBasemapSaved(true);
      setTimeout(() => setBasemapSaved(false), 2500);
    },
    onError: (err) => setBasemapError(err.message),
  });

  const [updateSettings, { loading: saving }] = useMutation(ADMIN_UPDATE_PLATFORM_SETTINGS_MUTATION, {
    onCompleted: () => {
      setSaved(true);
      setTimeout(() => setSaved(false), 2500);
    },
    onError: (err) => setSaveError(err.message),
  });

  useEffect(() => {
    if (data?.platformSettings) {
      setUsersAllowedToRegister(data.platformSettings.usersAllowedToRegister);
      setDefaultUserPlanId(data.platformSettings.defaultUserPlan?.id ?? '');
      setNewUserPermissions(permissionsOf(data.platformSettings.newUserPermissions));
    }
  }, [data]);

  const handleSave = async () => {
    setSaveError('');
    await updateSettings({
      variables: {
        usersAllowedToRegister,
        defaultUserPlanId: defaultUserPlanId || null,
        newUserPermissions,
      },
    });
  };

  return (
    <DashboardLayout>
      <div className="space-y-8">
        <div>
          <h2 className="text-3xl font-bold tracking-tight text-on-surface font-headline">Platform Settings</h2>
          <p className="text-on-surface-variant mt-1">Global configuration for the entire platform.</p>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-16 text-on-surface-variant">
            <Icon name="refresh" className="animate-spin mr-2" />
            Loading settings...
          </div>
        ) : settingsError && !data ? (
          <div className={styles.settingsCard}>
            <div className={styles.sectionHeader}>
              <Icon name="manage_accounts" className="text-blue-600" />
              <h3 className={styles.sectionTitle}>User Registration</h3>
            </div>
            <p className={styles.loadError}>{loadFailed('registration settings', settingsError.message)}</p>
          </div>
        ) : (
          <div className={styles.settingsCard}>
            <div className={styles.sectionHeader}>
              <Icon name="manage_accounts" className="text-blue-600" />
              <h3 className={styles.sectionTitle}>User Registration</h3>
            </div>

            <div className={styles.settingRow}>
              <div className={styles.settingInfo}>
                <label htmlFor="ps-register" className={styles.settingLabel}>Users allowed to register</label>
                <p className={styles.settingDescription}>
                  When disabled, the registration page is inaccessible and the "Create an account" link is hidden on the login page. Existing users are not affected.
                </p>
              </div>
              <label className={styles.toggle}>
                <input
                  id="ps-register"
                  type="checkbox"
                  checked={usersAllowedToRegister}
                  onChange={(e) => setUsersAllowedToRegister(e.target.checked)}
                />
                <span className={styles.toggleSlider} />
              </label>
            </div>

            <div className={styles.settingRow}>
              <div className={styles.settingInfo}>
                <label htmlFor="ps-default-plan" className={styles.settingLabel}>Default plan for new registrations</label>
                <p className={styles.settingDescription}>
                  The plan automatically assigned when a new user registers. If left unset, new users have no plan and face no storage or download restrictions.
                </p>
              </div>
              <div className={styles.planSelect}>
                <select
                  id="ps-default-plan"
                  className="input-field"
                  value={defaultUserPlanId}
                  onChange={(e) => setDefaultUserPlanId(e.target.value)}
                >
                  <option value="">— No plan (unrestricted) —</option>
                  {userPlanOptions.map((plan) => (
                    <option key={plan.id} value={plan.id}>{plan.name}</option>
                  ))}
                </select>
              </div>
            </div>

            <div className={styles.settingRowStacked}>
              <div className={styles.settingInfo}>
                <p className={styles.settingLabel} id="ps-new-perms">Permissions for new registrations</p>
                <p className={styles.settingDescription}>
                  What a newly registered account can use from its first sign-in. Accounts that already exist keep theirs —
                  change those in All Users → Edit permissions. People invited to a team use their main user&apos;s permissions.
                </p>
              </div>
              <div className={styles.permGrid} role="group" aria-labelledby="ps-new-perms">
                {PERMISSIONS_META.map((p) => (
                  <label key={p.key} className={styles.permOption} title={p.description}>
                    <input
                      type="checkbox"
                      className="accent-primary"
                      checked={newUserPermissions[p.key]}
                      onChange={(e) => setNewUserPermissions((prev) => ({ ...prev, [p.key]: e.target.checked }))}
                    />
                    <span>
                      <span className={styles.permName}>{p.label}</span>
                      <span className={styles.permHint}>{p.description}</span>
                    </span>
                  </label>
                ))}
              </div>
            </div>

            <div className={styles.saveRow}>
              {saveError && <span className={styles.errorBadge}>{saveError}</span>}
              {saved && (
                <span className={styles.savedBadge}>
                  <Icon name="check_circle" className="text-sm" />
                  Saved
                </span>
              )}
              <button
                className="btn-primary py-2 px-6 rounded-lg disabled:opacity-70 disabled:cursor-not-allowed"
                onClick={handleSave}
                disabled={saving}
              >
                {saving ? (
                  <>
                    <Icon name="refresh" className="text-sm animate-spin" />
                    Saving...
                  </>
                ) : (
                  'Save Changes'
                )}
              </button>
            </div>
          </div>
        )}

        <div className={styles.settingsCard}>
          <div className={styles.sectionHeader}>
            <Icon name="shield" className="text-amber-600" />
            <h3 className={styles.sectionTitle}>Safety Configuration</h3>
          </div>

          <p className={styles.settingDescription} style={{ marginBottom: 4 }}>
            Hard limits applied to every project on the platform, whoever uploads. Leave a value at
            <strong> 0 </strong> for no limit. Plan-based storage and download limits are separate
            (admin → User Plans).
          </p>

          {!safetyReady && (safetyLoadError
            ? <p className={styles.loadError}>{loadFailed('safety limits', safetyLoadError.message)}</p>
            : <p className={styles.settingDescription}>Loading safety limits…</p>)}

          {safetyReady && SAFETY_GROUPS.map((group) => (
            <div key={group.title} className={styles.settingRow}>
              <div className={styles.settingInfo}>
                <p className={styles.settingLabel}>{group.title}</p>
                <p className={styles.settingDescription}>{group.hint}</p>
              </div>
              <div className={styles.limitGrid}>
                {group.fields.map((field) => (
                  <label key={field.key} className={styles.limitField}>
                    <span className={styles.limitLabel}>{field.label}</span>
                    <input
                      className="input-field"
                      type="number"
                      min={0}
                      step={1}
                      value={safety[field.key]}
                      onChange={(e) => setSafety((prev) => ({
                        ...prev,
                        [field.key]: Math.max(0, Math.floor(Number(e.target.value) || 0)),
                      }))}
                    />
                  </label>
                ))}
              </div>
            </div>
          ))}

          <div className={styles.saveRow}>
            {safetyError && <span className={styles.errorBadge}>{safetyError}</span>}
            {safetySaved && (
              <span className={styles.savedBadge}>
                <Icon name="check_circle" className="text-sm" />
                Saved
              </span>
            )}
            <button
              className="btn-primary py-2 px-6 rounded-lg disabled:opacity-70 disabled:cursor-not-allowed"
              onClick={() => { setSafetyError(''); updateSafety({ variables: { input: safety } }); }}
              disabled={savingSafety || !safetyReady}
            >
              {savingSafety ? (
                <>
                  <Icon name="refresh" className="text-sm animate-spin" />
                  Saving...
                </>
              ) : (
                'Save Safety Limits'
              )}
            </button>
          </div>
        </div>

        <FileFormatsSettings />

        <div className={styles.settingsCard}>
          <div className={styles.sectionHeader}>
            <Icon name="map" className="text-emerald-700" />
            <h3 className={styles.sectionTitle}>Base Map</h3>
          </div>

          <p className={styles.settingDescription} style={{ marginBottom: 4 }}>
            The Scene can draw a real map (satellite, streets, topographic) under a project's
            point clouds, orthomosaics, models and drawings. Every provider with a key below
            appears in the Scene's Base map list; without any key only OpenStreetMap's street
            tiles are offered, which their usage policy limits to light use.
          </p>

          <div className={styles.settingRow}>
            <div className={styles.settingInfo}>
              <label htmlFor="ps-esri-key" className={styles.settingLabel}>Esri (ArcGIS) API key</label>
              <p className={styles.settingDescription}>
                Create it at developers.arcgis.com → ArcGIS Location Platform → API keys, with the
                Basemaps privilege, and restrict its referrers to your domains (infrio.cloud,
                test.infrio.cloud) — the key is visible to every browser that opens a Scene.
                The free tier covers about two million tiles a month.
              </p>
            </div>
          </div>
          <input
            id="ps-esri-key"
            className="input-field"
            type="text"
            autoComplete="off"
            spellCheck={false}
            placeholder="AAPK…"
            value={basemap.esriApiKey}
            onChange={(e) => setBasemap((prev) => ({ ...prev, esriApiKey: e.target.value }))}
          />

          <div className={styles.settingRow}>
            <div className={styles.settingInfo}>
              <label htmlFor="ps-google-key" className={styles.settingLabel}>Google Maps API key</label>
              <p className={styles.settingDescription}>
                Google Cloud Console → APIs &amp; Services → enable the <strong>Map Tiles API</strong>
                (billing must be on) → Credentials → API key, restricted to the Map Tiles API and
                to your domains as HTTP referrers. Adds Satellite, Satellite + roads, Roadmap and
                Terrain from Google. Billed per 1,000 tile requests after Google's monthly free
                usage; the Scene asks for 512 px tiles, a quarter of the requests of normal ones.
                {googleStatus === 'ok' && <span className={styles.savedBadge} style={{ marginLeft: 8 }}>Key works</span>}
                {googleStatus.startsWith('error') && <span className={styles.errorBadge} style={{ marginLeft: 8 }}>{googleStatus}</span>}
              </p>
            </div>
          </div>
          <input
            id="ps-google-key"
            className="input-field"
            type="text"
            autoComplete="off"
            spellCheck={false}
            placeholder="AIza…"
            value={basemap.googleApiKey}
            onChange={(e) => setBasemap((prev) => ({ ...prev, googleApiKey: e.target.value }))}
          />

          <div className={styles.settingRow}>
            <div className={styles.settingInfo}>
              <label htmlFor="ps-terrain" className={styles.settingLabel}>Terrain</label>
              <p className={styles.settingDescription}>
                Drape the map over elevation (AWS Terrain Tiles, free, 30 m data) so hilly sites do
                not sink into a flat plane. Each project can still switch it off in the Scene.
              </p>
            </div>
            <label className={styles.toggle}>
              <input
                id="ps-terrain"
                type="checkbox"
                checked={basemap.terrainEnabled}
                onChange={(e) => setBasemap((prev) => ({ ...prev, terrainEnabled: e.target.checked }))}
              />
              <span className={styles.toggleSlider} />
            </label>
          </div>

          {!basemapReady && basemapLoadError && <p className={styles.loadError}>{loadFailed('base map settings', basemapLoadError.message)}</p>}

          <div className={styles.saveRow}>
            {basemapError && <span className={styles.errorBadge}>{basemapError}</span>}
            {basemapSaved && (
              <span className={styles.savedBadge}>
                <Icon name="check_circle" className="text-sm" />
                Saved
              </span>
            )}
            <button
              className="btn-primary py-2 px-6 rounded-lg disabled:opacity-70 disabled:cursor-not-allowed"
              onClick={() => { setBasemapError(''); updateBasemap({ variables: { input: basemap } }); }}
              disabled={savingBasemap || !basemapReady}
            >
              {savingBasemap ? (
                <>
                  <Icon name="refresh" className="text-sm animate-spin" />
                  Saving...
                </>
              ) : (
                'Save Base Map Settings'
              )}
            </button>
          </div>
        </div>

        <div className={styles.settingsCard}>
          <div className={styles.sectionHeader}>
            <Icon name="cloud_sync" className="text-sky-700" />
            <h3 className={styles.sectionTitle}>Storage &amp; Delivery</h3>
          </div>

          <p className={styles.settingDescription} style={{ marginBottom: 4 }}>
            How the bucket stores files and how downloads reach people. Both switches describe setup
            done once in the AWS Console (steps below) — turn each one on once its AWS step is done.
            Cost Analysis and the calculator price storage and downloads from them.
          </p>

          <div className={styles.settingRow}>
            <div className={styles.settingInfo}>
              <label htmlFor="ps-delivery-tiering" className={styles.settingLabel}>Files are in S3 Intelligent-Tiering</label>
              <p className={styles.settingDescription}>
                Files nobody opened for 30 days cost about 45% less, after 90 days about 80% less, and
                the next open puts them back at full price — no retrieval fees, no waiting. Storage is
                then priced from each file&apos;s own opens, downloads and conversions.
                {deliveryInfo?.intelligentTiering && deliveryInfo.intelligentTieringSince && (
                  <> On since <strong>{sinceDate(deliveryInfo.intelligentTieringSince)}</strong> — earlier storage is priced as Standard.</>
                )}
              </p>
            </div>
            <label className={styles.toggle}>
              <input
                id="ps-delivery-tiering"
                type="checkbox"
                checked={delivery.intelligentTiering}
                onChange={(e) => setDeliveryField('intelligentTiering', e.target.checked)}
              />
              <span className={styles.toggleSlider} />
            </label>
          </div>

          <div className={styles.settingRow}>
            <div className={styles.settingInfo}>
              <label htmlFor="ps-delivery-downloads" className={styles.settingLabel}>Downloads through CloudFront</label>
              <p className={styles.settingDescription}>
                Download links are signed through the CloudFront distribution, so a download uses the
                CloudFront plan&apos;s allowance instead of paying S3 egress. Files over{' '}
                {deliveryInfo?.maxCdnDownloadGb ?? 50} GB, archived files and files uploaded before
                this release keep downloading from S3.
              </p>
              {deliveryInfo && !deliveryInfo.cloudfrontConfigured && (
                <p className={styles.settingWarning}>
                  This server has no CloudFront distribution configured, so its downloads stay on S3
                  whatever this switch says (expected on QA).
                </p>
              )}
            </div>
            <label className={styles.toggle}>
              <input
                id="ps-delivery-downloads"
                type="checkbox"
                checked={delivery.downloadsViaCloudfront}
                onChange={(e) => setDeliveryField('downloadsViaCloudfront', e.target.checked)}
              />
              <span className={styles.toggleSlider} />
            </label>
          </div>

          <div className={styles.infoBox}>
            <p className={styles.infoBoxTitle}>
              <Icon name="info" className="text-base" />
              AWS setup behind these switches
            </p>
            <ol>
              <li>
                <strong>Intelligent-Tiering</strong>, once per bucket: S3 → bucket →{' '}
                <strong>Management</strong> → <strong>Create lifecycle rule</strong> named{' '}
                <strong>intelligent-tiering</strong> → scope <strong>Apply to all objects in the
                bucket</strong> → &quot;Transition current versions of objects between storage
                classes&quot; → <strong>Intelligent-Tiering</strong> after <strong>0 days</strong>.
                S3 leaves objects under 128 KB (thumbnails) in Standard by itself. Existing files move
                within a day or two; each one starts its 30-day clock then.
              </li>
              <li>
                Keep <strong>archive-raw</strong> from File Archiving below: originals still move from
                Intelligent-Tiering to Glacier at that age.
              </li>
              <li>
                <strong>Downloads</strong>: nothing new in CloudFront — the distribution&apos;s default
                behaviour already serves <code className={styles.infoCode}>raw/</code> with the same
                signed URLs. Only check the bucket policy lets the distribution read the whole bucket
                (<code className={styles.infoCode}>arn:aws:s3:::bucket-name/*</code>).
              </li>
            </ol>
          </div>

          {!deliveryInfo && deliveryLoadError && <p className={styles.loadError}>{loadFailed('storage & delivery settings', deliveryLoadError.message)}</p>}

          <div className={styles.saveRow}>
            {deliveryError && <span className={styles.errorBadge}>{deliveryError}</span>}
            {deliverySaved && (
              <span className={styles.savedBadge}>
                <Icon name="check_circle" className="text-sm" />
                Saved
              </span>
            )}
            <button
              className="btn-primary py-2 px-6 rounded-lg disabled:opacity-70 disabled:cursor-not-allowed"
              onClick={() => { setDeliveryError(''); updateDelivery({ variables: { input: delivery } }); }}
              disabled={savingDelivery || !deliveryInfo}
            >
              {savingDelivery ? (
                <>
                  <Icon name="refresh" className="text-sm animate-spin" />
                  Saving...
                </>
              ) : (
                'Save Storage & Delivery'
              )}
            </button>
          </div>
        </div>

        <div className={styles.settingsCard}>
          <div className={styles.sectionHeader}>
            <Icon name="archive" className="text-cyan-700" />
            <h3 className={styles.sectionTitle}>File Archiving</h3>
          </div>

          <p className={styles.settingDescription} style={{ marginBottom: 4 }}>
            Moves old files to cheap cold storage in two stages. Stage 1: files stay viewable but
            downloading the original becomes a retrieval request (3–5 hours, email when ready).
            Stage 2: the file is fully archived — not viewable, not downloadable — until someone
            presses Unarchive. Ages count from each file's upload date. S3 storage mode only.
          </p>

          <div className={styles.settingRow}>
            <div className={styles.settingInfo}>
              <label htmlFor="ps-archive-enabled" className={styles.settingLabel}>Archiving enabled</label>
              <p className={styles.settingDescription}>
                Turning this on only flips the app's behaviour — the bytes move via the S3 lifecycle
                rules below, which you set once in the AWS Console. No restart needed; the archive
                sweep (admin → Scheduled Tasks) picks the change up at its next run.
              </p>
            </div>
            <label className={styles.toggle}>
              <input
                id="ps-archive-enabled"
                type="checkbox"
                checked={archive.enabled}
                onChange={(e) => setArchiveField('enabled', e.target.checked)}
              />
              <span className={styles.toggleSlider} />
            </label>
          </div>

          <div className={styles.settingRow}>
            <div className={styles.settingInfo}>
              <label htmlFor="ps-archive-delayed" className={styles.settingLabel}>Delayed download after (days)</label>
              <p className={styles.settingDescription}>
                Stage 1. After this age the original goes cold: viewing keeps working (octrees,
                panoramas, drawings and meshes stay hot), but downloading the original becomes a
                request. Documents are the exception — viewing a PDF reads the original, so they
                behave like stage 2 from here. Minimum 30.
              </p>
            </div>
            <div className={styles.daysInput}>
              <input
                id="ps-archive-delayed"
                className="input-field"
                type="number"
                min={30}
                step={1}
                value={archive.delayedDownloadDays}
                onChange={(e) => setArchiveField('delayedDownloadDays', wholeDays(e.target.value))}
              />
            </div>
          </div>

          <div className={styles.settingRow}>
            <div className={styles.settingInfo}>
              <label htmlFor="ps-archive-full" className={styles.settingLabel}>Full archive after (days)</label>
              <p className={styles.settingDescription}>
                Stage 2. After this age the file is archived entirely — no viewing, no download.
                Project members see an Unarchive button that restores everything (3–5 hours, one
                email per project when ready). At least 30 days after stage 1, or{' '}
                <strong>0 = never</strong>: converted files stay in S3 and viewable for good (with
                Intelligent-Tiering, idle ones already cost about as little).
              </p>
            </div>
            <div className={styles.daysInput}>
              <input
                id="ps-archive-full"
                className="input-field"
                type="number"
                min={0}
                step={1}
                value={archive.fullArchiveDays}
                onChange={(e) => setArchiveField('fullArchiveDays', wholeDays(e.target.value))}
              />
            </div>
          </div>

          <div className={styles.settingRow}>
            <div className={styles.settingInfo}>
              <label htmlFor="ps-archive-keep" className={styles.settingLabel}>Restored copy kept (days)</label>
              <p className={styles.settingDescription}>
                How long a retrieved temporary copy stays usable before it lapses back to the
                archive. 1–30 days.
              </p>
            </div>
            <div className={styles.daysInput}>
              <input
                id="ps-archive-keep"
                className="input-field"
                type="number"
                min={1}
                max={30}
                step={1}
                value={archive.restoreKeepDays}
                onChange={(e) => setArchiveField('restoreKeepDays', wholeDays(e.target.value))}
              />
            </div>
          </div>

          <div className={styles.infoBox}>
            <p className={styles.infoBoxTitle}>
              <Icon name="info" className="text-base" />
              S3 lifecycle rules to mirror these settings
            </p>
            <p>
              Set once per bucket (<code className={styles.infoCode}>geodraft-files</code> and{' '}
              <code className={styles.infoCode}>geodraft-qa-files</code>) in the AWS Console: S3 →
              bucket → <strong>Management</strong> → <strong>Create lifecycle rule</strong>. The app
              switches 7 days before the bytes actually move, so each rule uses the setting + 7:
            </p>
            <ol>
              <li>
                Rule <strong>archive-raw</strong>: filter by prefix{' '}
                <code className={styles.infoCode}>raw/</code> → "Transition current versions of
                objects between storage classes" → <strong>Glacier Flexible Retrieval</strong> after{' '}
                <strong>{archive.delayedDownloadDays + 7} days</strong>.
              </li>
              {archive.fullArchiveDays > 0 ? (
                <li>
                  One rule per derived prefix (<code className={styles.infoCode}>potree/</code>,{' '}
                  <code className={styles.infoCode}>panoramas/</code>,{' '}
                  <code className={styles.infoCode}>2d/</code>,{' '}
                  <code className={styles.infoCode}>3d/</code>,{' '}
                  <code className={styles.infoCode}>ifc/</code>,{' '}
                  <code className={styles.infoCode}>gaussian/</code>,{' '}
                  <code className={styles.infoCode}>ortho/</code>,{' '}
                  <code className={styles.infoCode}>video360/</code>): same transition to{' '}
                  <strong>Glacier Flexible Retrieval</strong> after{' '}
                  <strong>{archive.fullArchiveDays + 7} days</strong>.
                </li>
              ) : (
                <li>
                  No full archive: <strong>no rules</strong> for the derived prefixes — delete any{' '}
                  <strong>archive-potree</strong>, <strong>archive-panoramas</strong>,{' '}
                  <strong>archive-2d</strong>, <strong>archive-3d</strong>, <strong>archive-ifc</strong>,{' '}
                  <strong>archive-gaussian</strong>, <strong>archive-ortho</strong> or{' '}
                  <strong>archive-video360</strong> rule. Anything they already moved stays in Glacier
                  until its project is unarchived.
                </li>
              )}
              <li>
                Do NOT add rules for <code className={styles.infoCode}>thumbnails/</code> or{' '}
                <code className={styles.infoCode}>annotations/</code> — they stay hot so archived
                projects still show their file lists and notes.
              </li>
              <li>
                IAM: the backend's user/role needs <code className={styles.infoCode}>s3:RestoreObject</code>{' '}
                on both buckets (IAM → the backend's policy → add the action next to GetObject).
              </li>
            </ol>
            <p>
              After changing the day values here, update the day counts in those rules to match
              (setting + 7). Rules only affect objects going forward — already-transitioned objects
              stay where they are.
            </p>
          </div>

          {!archiveReady && archiveLoadError && <p className={styles.loadError}>{loadFailed('archiving settings', archiveLoadError.message)}</p>}

          <div className={styles.saveRow}>
            {archiveError && <span className={styles.errorBadge}>{archiveError}</span>}
            {archiveSaved && (
              <span className={styles.savedBadge}>
                <Icon name="check_circle" className="text-sm" />
                Saved
              </span>
            )}
            <button
              className="btn-primary py-2 px-6 rounded-lg disabled:opacity-70 disabled:cursor-not-allowed"
              onClick={handleSaveArchive}
              disabled={savingArchive || !archiveReady}
            >
              {savingArchive ? (
                <>
                  <Icon name="refresh" className="text-sm animate-spin" />
                  Saving...
                </>
              ) : (
                'Save Archiving Settings'
              )}
            </button>
          </div>
        </div>

        <div className={styles.settingsCard}>
          <div className={styles.sectionHeader}>
            <Icon name="delete" className="text-red-700" />
            <h3 className={styles.sectionTitle}>Trash</h3>
          </div>

          <p className={styles.settingDescription} style={{ marginBottom: 4 }}>
            Deleted files and projects (with every file in them) go to Trash. Their bytes stay in S3 and
            count on the owner&apos;s storage until they are removed for good — then they stop costing
            anything and can no longer be restored. Owners see their Trash, with a countdown, under
            Activity Logs. The removal itself is the Trash removal task in Scheduled Tasks.
          </p>

          <div className={styles.settingRow}>
            <div className={styles.settingInfo}>
              <label htmlFor="ps-trash-days" className={styles.settingLabel}>Remove for good after (days)</label>
              <p className={styles.settingDescription}>
                Counted from the deletion. <strong>0 = never</strong>: nothing is removed automatically.
              </p>
              {trashOverview && (
                <p className={styles.settingDescription}>
                  In Trash now: {trashOverview.files} file{trashOverview.files === 1 ? '' : 's'} ({formatBytes(trashOverview.bytes)}) and{' '}
                  {trashOverview.projects} project{trashOverview.projects === 1 ? '' : 's'}.
                  {trash.retentionDays > 0 && (
                    <> With this value, <strong>{trashOverview.dueFiles} file{trashOverview.dueFiles === 1 ? '' : 's'} ({formatBytes(trashOverview.dueBytes)})
                    and {trashOverview.dueProjects} project{trashOverview.dueProjects === 1 ? '' : 's'}</strong> are due at the next run.</>
                  )}
                </p>
              )}
            </div>
            <div className={styles.daysInput}>
              <input
                id="ps-trash-days"
                className="input-field"
                type="number"
                min={0}
                step={1}
                value={trash.retentionDays}
                onChange={(e) => { setTrashError(''); setTrash((t) => ({ ...t, retentionDays: wholeDays(e.target.value) })); }}
              />
            </div>
          </div>

          <div className={styles.settingRow}>
            <div className={styles.settingInfo}>
              <label htmlFor="ps-trash-now" className={styles.settingLabel}>Users can delete from Trash at once</label>
              <p className={styles.settingDescription}>
                Shows a Delete now button in each user&apos;s Trash, which removes the item for good right
                away and frees its storage.
              </p>
            </div>
            <label className={styles.toggle}>
              <input
                id="ps-trash-now"
                type="checkbox"
                checked={trash.allowImmediateDelete}
                onChange={(e) => { setTrashError(''); setTrash((t) => ({ ...t, allowImmediateDelete: e.target.checked })); }}
              />
              <span className={styles.toggleSlider} />
            </label>
          </div>

          <div className={styles.settingRow}>
            <div className={styles.settingInfo}>
              <label htmlFor="ps-trash-gb" className={styles.settingLabel}>GB each user can delete at once per month</label>
              <p className={styles.settingDescription}>
                Stops uploading and deleting over and over to stay under a plan. Counted per calendar
                month; anything over it still leaves when its Trash time is up. <strong>0 = no limit</strong>.
              </p>
            </div>
            <div className={styles.daysInput}>
              <input
                id="ps-trash-gb"
                className="input-field"
                type="number"
                min={0}
                step={1}
                disabled={!trash.allowImmediateDelete}
                value={trash.immediateDeleteGbPerMonth}
                onChange={(e) => { setTrashError(''); setTrash((t) => ({ ...t, immediateDeleteGbPerMonth: Math.max(0, Number(e.target.value) || 0) })); }}
              />
            </div>
          </div>

          {!trashReady && trashLoadError && <p className={styles.loadError}>{loadFailed('Trash settings', trashLoadError.message)}</p>}

          <div className={styles.saveRow}>
            {trashError && <span className={styles.errorBadge}>{trashError}</span>}
            {trashSaved && (
              <span className={styles.savedBadge}>
                <Icon name="check_circle" className="text-sm" />
                Saved
              </span>
            )}
            <button
              className="btn-primary py-2 px-6 rounded-lg disabled:opacity-70 disabled:cursor-not-allowed"
              onClick={handleSaveTrash}
              disabled={savingTrash || !trashReady}
            >
              {savingTrash ? (
                <>
                  <Icon name="refresh" className="text-sm animate-spin" />
                  Saving...
                </>
              ) : (
                'Save Trash Settings'
              )}
            </button>
          </div>
        </div>

        <div className={styles.settingsCard}>
          <div className={styles.sectionHeader}>
            <Icon name="view_in_ar" className="text-[#0061ff]" />
            <h3 className={styles.sectionTitle}>Scene</h3>
          </div>

          <p className={styles.settingDescription} style={{ marginBottom: 4 }}>
            How the keyboard (W A S D, the arrows, Q and E), the mouse wheel and a two-finger pinch on
            touch screens move the camera in the Scene and the photoreal viewer: a wheel notch, or 100 px
            of finger spread, is 0.3 s of travel. The pace is the walking speed plus the speed-up for the
            open space ahead of the camera, so flying over a site is quick and walking a room is not;
            what was last clicked plays no part. Dragging keeps scaling with what you grabbed. Open
            Scenes pick the new values up the next time they load.
          </p>

          <div className={styles.settingRow}>
            <div className={styles.settingInfo}>
              <label htmlFor="ps-scene-walk" className={styles.settingLabel}>Walking speed (m/s)</label>
              <p className={styles.settingDescription}>
                Metres a second while a key is held. A brisk walk is 1.5, a bicycle 5, a car in town 14.
              </p>
            </div>
            <div className={styles.daysInput}>
              <input
                id="ps-scene-walk"
                className="input-field"
                type="number"
                min={0.1}
                step={0.5}
                value={scene.walkSpeedMps}
                onChange={(e) => { setSceneError(''); setScene((s) => ({ ...s, walkSpeedMps: Number(e.target.value) })); }}
              />
            </div>
          </div>

          <div className={styles.settingRow}>
            <div className={styles.settingInfo}>
              <label htmlFor="ps-scene-run" className={styles.settingLabel}>Run multiplier (Shift)</label>
              <p className={styles.settingDescription}>
                How many times faster the camera moves while Shift is held. 1 = no difference.
              </p>
            </div>
            <div className={styles.daysInput}>
              <input
                id="ps-scene-run"
                className="input-field"
                type="number"
                min={1}
                step={0.5}
                value={scene.runMultiplier}
                onChange={(e) => { setSceneError(''); setScene((s) => ({ ...s, runMultiplier: Number(e.target.value) })); }}
              />
            </div>
          </div>

          <div className={styles.settingRow}>
            <div className={styles.settingInfo}>
              <label htmlFor="ps-scene-far" className={styles.settingLabel}>Speed-up with distance (m/s per metre ahead)</label>
              <p className={styles.settingDescription}>
                Added to the walking speed for every metre of open space in front of the camera: at 0.5,
                a camera 200 m above a site flies at 100 m/s and slows as it comes down, never below the
                walking speed. <strong>0</strong> = always the walking speed.
              </p>
            </div>
            <div className={styles.daysInput}>
              <input
                id="ps-scene-far"
                className="input-field"
                type="number"
                min={0}
                step={0.1}
                value={scene.distanceFactor}
                onChange={(e) => { setSceneError(''); setScene((s) => ({ ...s, distanceFactor: Number(e.target.value) })); }}
              />
            </div>
          </div>

          {!sceneReady && sceneLoadError && <p className={styles.loadError}>{loadFailed('Scene settings', sceneLoadError.message)}</p>}

          <div className={styles.saveRow}>
            {sceneError && <span className={styles.errorBadge}>{sceneError}</span>}
            {sceneSaved && (
              <span className={styles.savedBadge}>
                <Icon name="check_circle" className="text-sm" />
                Saved
              </span>
            )}
            <button
              className="btn-primary py-2 px-6 rounded-lg disabled:opacity-70 disabled:cursor-not-allowed"
              onClick={handleSaveScene}
              disabled={savingScene || !sceneReady}
            >
              {savingScene ? (
                <>
                  <Icon name="refresh" className="text-sm animate-spin" />
                  Saving...
                </>
              ) : (
                'Save Scene Settings'
              )}
            </button>
          </div>
        </div>

        <div className={styles.settingsCard}>
          <div className={styles.sectionHeader}>
            <Icon name="mail" className="text-[#71717a]" />
            <h3 className={styles.sectionTitle}>Email Configuration</h3>
          </div>

          <div className={styles.settingRow}>
            <div className={styles.settingInfo}>
              <label htmlFor="ps-email-enabled" className={styles.settingLabel}>Email sending enabled</label>
              <p className={styles.settingDescription}>
                When enabled, the platform sends notification emails (e.g. "your file finished converting").
                Templates live in the codebase; the delivery address per user is their profile's
                "Send notifications to" field, falling back to their account email.
              </p>
            </div>
            <label className={styles.toggle}>
              <input
                id="ps-email-enabled"
                type="checkbox"
                checked={email.enabled}
                onChange={(e) => setEmail((p) => ({ ...p, enabled: e.target.checked }))}
              />
              <span className={styles.toggleSlider} />
            </label>
          </div>

          <div className={styles.settingRow}>
            <div className={styles.settingInfo}>
              <p className={styles.settingLabel}>Delivery provider</p>
              <p className={styles.settingDescription}>
                Amazon SES uses the server's IAM role — no credentials, but the sender domain must be
                verified in SES. SMTP works with any mail provider using host + credentials.
              </p>
            </div>
            <div className={styles.segmented}>
              {(['ses', 'smtp'] as const).map((prov) => (
                <button
                  key={prov}
                  type="button"
                  className={email.provider === prov ? `${styles.segBtn} ${styles.segBtnActive}` : styles.segBtn}
                  onClick={() => setEmail((p) => ({ ...p, provider: prov }))}
                >
                  {prov === 'ses' ? 'Amazon SES' : 'SMTP'}
                </button>
              ))}
            </div>
          </div>

          {!emailReady && emailLoadError && <p className={styles.loadError}>{loadFailed('email settings', emailLoadError.message)}</p>}

          <div className={styles.emailGrid}>
            <div className="space-y-1.5">
              <label htmlFor="ps-email-from-name" className={styles.fieldLabel}>Sender name</label>
              <input
                id="ps-email-from-name"
                className="input-field"
                placeholder="Infrio"
                value={email.fromName}
                onChange={(e) => setEmail((p) => ({ ...p, fromName: e.target.value }))}
              />
              <p className={styles.fieldHint}>Shown as the sender in the inbox (e.g. "Infrio"). Leave empty to use the address only.</p>
            </div>
            <div className="space-y-1.5">
              <label htmlFor="ps-email-from-address" className={styles.fieldLabel}>From address</label>
              <input
                id="ps-email-from-address"
                className="input-field"
                placeholder="no-reply@infrio.cloud"
                value={email.fromAddress}
                onChange={(e) => setEmail((p) => ({ ...p, fromAddress: e.target.value }))}
              />
            </div>
            <div className="space-y-1.5">
              <label htmlFor="ps-email-app-url" className={styles.fieldLabel}>Platform URL (used in email links)</label>
              <input
                id="ps-email-app-url"
                className="input-field"
                placeholder="https://infrio.cloud"
                value={email.appUrl}
                onChange={(e) => setEmail((p) => ({ ...p, appUrl: e.target.value }))}
              />
            </div>

            {email.provider === 'ses' ? (
              <div className="space-y-1.5">
                <label htmlFor="ps-email-ses-region" className={styles.fieldLabel}>SES region</label>
                <input
                  id="ps-email-ses-region"
                  className="input-field"
                  placeholder="eu-west-3"
                  value={email.sesRegion}
                  onChange={(e) => setEmail((p) => ({ ...p, sesRegion: e.target.value }))}
                />
                <p className={styles.fieldHint}>The sender's domain must be a verified identity in SES, and the server role needs ses:SendEmail.</p>
              </div>
            ) : (
              <>
                <div className="space-y-1.5">
                  <label htmlFor="ps-email-smtp-host" className={styles.fieldLabel}>SMTP host</label>
                  <input
                    id="ps-email-smtp-host"
                    className="input-field"
                    placeholder="smtp.example.com"
                    value={email.smtpHost}
                    onChange={(e) => setEmail((p) => ({ ...p, smtpHost: e.target.value }))}
                  />
                </div>
                <div className="space-y-1.5">
                  <label htmlFor="ps-email-smtp-port" className={styles.fieldLabel}>SMTP port</label>
                  <input
                    id="ps-email-smtp-port"
                    className="input-field"
                    type="number"
                    value={email.smtpPort}
                    onChange={(e) => setEmail((p) => ({ ...p, smtpPort: Number(e.target.value) }))}
                  />
                  <label className={styles.inlineCheck}>
                    <input
                      type="checkbox"
                      checked={email.smtpSecure}
                      onChange={(e) => setEmail((p) => ({ ...p, smtpSecure: e.target.checked }))}
                    />
                    Use TLS/SSL (port 465). Untick for STARTTLS on 587.
                  </label>
                </div>
                <div className="space-y-1.5">
                  <label htmlFor="ps-email-smtp-username" className={styles.fieldLabel}>SMTP username</label>
                  <input
                    id="ps-email-smtp-username"
                    className="input-field"
                    autoComplete="off"
                    value={email.smtpUsername}
                    onChange={(e) => setEmail((p) => ({ ...p, smtpUsername: e.target.value }))}
                  />
                </div>
                <div className="space-y-1.5">
                  <label htmlFor="ps-email-smtp-password" className={styles.fieldLabel}>SMTP password</label>
                  <input
                    id="ps-email-smtp-password"
                    className="input-field"
                    type="password"
                    autoComplete="new-password"
                    placeholder={email.smtpPasswordSet ? '•••••••• (leave empty to keep current)' : ''}
                    value={email.smtpPassword}
                    onChange={(e) => setEmail((p) => ({ ...p, smtpPassword: e.target.value }))}
                  />
                </div>
              </>
            )}
          </div>

          <div className={styles.saveRow}>
            <div className={styles.testRow}>
              <input
                aria-label="Send a test email to"
                className="input-field"
                type="email"
                placeholder="you@company.com"
                value={testTo}
                onChange={(e) => { setTestTo(e.target.value); setTestResult(null); }}
              />
              <button
                className="px-4 py-2 rounded-lg text-sm font-semibold border border-outline-variant text-on-surface hover:bg-surface-container-low transition-colors disabled:opacity-50 whitespace-nowrap"
                onClick={() => testTo.trim() && sendTestEmail({ variables: { to: testTo.trim() } })}
                disabled={sendingTest || !testTo.trim()}
              >
                {sendingTest ? 'Sending…' : 'Send test email'}
              </button>
            </div>
            {testResult && (
              <span className={testResult.ok ? styles.savedBadge : styles.errorBadge}>
                {testResult.ok ? 'Test email sent — check the inbox' : (testResult.error || 'Test failed')}
              </span>
            )}
            {emailError && <span className={styles.errorBadge}>{emailError}</span>}
            {emailSaved && (
              <span className={styles.savedBadge}>
                <Icon name="check_circle" className="text-sm" />
                Saved
              </span>
            )}
            <button
              className="btn-primary py-2 px-6 rounded-lg disabled:opacity-70 disabled:cursor-not-allowed"
              onClick={handleSaveEmail}
              disabled={savingEmail || !emailReady}
            >
              {savingEmail ? 'Saving…' : 'Save Email Settings'}
            </button>
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default AdminPlatformSettings;
